import { useQuery, useQueryClient } from "@tanstack/react-query";
import * as React from "react";

import { useNavigation } from "@/providers";
import { useDomainServices } from "@/providers/DomainServices";
import { useInferDevices } from "@/shared/domain/devices/hooks/useInferDevices";
import { type FileRecord } from "@/shared/domain/files";
import { useFileRecords } from "@/shared/domain/files/hooks";
import {
  checkSessionCreation,
  prepareSessionComposition,
  type CreateSessionRequest,
  type SessionCompositionOutcome,
  type SessionCreationResult,
} from "@/shared/domain/sessions";
import { useCreateSessionFromFiles } from "@/shared/domain/sessions/hooks";
import { sessionQueryKeys } from "@/shared/domain/sessions/hooks/sessionQueryKeys";
import { userFacingErrorMessage } from "@/shared/http";

interface UseCreateSessionFormParams {
  orgId: string;
  /**
   * Identifies this visit to the page (`location.key`). A same-route
   * renavigation gets a new key, which abandons a creation started under the
   * old one instead of letting its result land on the new form.
   */
  pageKey?: string;
  datasetId: string | undefined;
  fileIds: string[] | undefined;
  directoryPatterns: string[] | undefined;
}

/** The one creation attempt this form owns, from prepare through cleanup. */
interface CreationOperation {
  contextKey: string;
  /** Set when the user, an unmount, or a context change abandons the attempt. */
  stopped: boolean;
}

export interface CreateSessionForm {
  name: string;
  setName: (name: string) => void;

  selectedFiles: FileRecord[];
  isPrefilling: boolean;
  prefillError: Error | null;
  mergeFiles: (incoming: FileRecord[]) => void;
  removeFiles: (fileIds: string[]) => void;

  inferredDeviceIds: string[];
  isInferringDevices: boolean;
  inferDevicesError: Error | null;
  explicitDeviceIds: string[];
  addDevice: (deviceId: string) => void;
  removeDevice: (deviceId: string) => void;

  customFieldValues: Record<string, unknown>;
  setCustomFieldValues: (values: Record<string, unknown>) => void;

  submitError: React.ReactNode;
  isSubmitting: boolean;
  submit: () => Promise<void>;

  /** Per-file progress of the attempt, published as each batch lands. */
  outcome?: SessionCompositionOutcome;
  /** Present once the attempt settles, including when it settled badly. */
  creationResult?: SessionCreationResult;
  /** A cache refresh or a session check is running. */
  isRecovering: boolean;
  /**
   * False while the prefill is in flight or failed (submitting then would
   * create a session without the entry point's seeded files), and while an
   * attempt is held, until `prepareNewCreation` releases it.
   */
  canSubmit: boolean;
  /**
   * No attempt is held, or the held one was started by this page visit, for
   * this org and seed.
   */
  contextMatches: boolean;
  /**
   * Abandon the attempt: no further batches are sent, and a session left
   * holding only some of the files is deleted.
   */
  stop: () => void;
  /** Ask the server whether a session the client failed to delete still exists. */
  checkCreation: () => Promise<void>;
  refreshSessions: () => Promise<void>;
  /** Release the held attempt so the draft can be submitted again. */
  prepareNewCreation: () => void;
}

/**
 * Owns the Create-session form: the editable name, the working file selection
 * (prefilled from the entry point), the inferred/explicit device sets, the
 * custom-field values, the submit flow, and the recovery actions for a creation
 * attempt that did not complete. The page component renders against this state
 * and holds no data logic of its own.
 */
export function useCreateSessionForm({
  orgId,
  pageKey,
  datasetId,
  fileIds,
  directoryPatterns,
}: UseCreateSessionFormParams): CreateSessionForm {
  const { goto } = useNavigation();
  const { datasetService, sessionService } = useDomainServices();
  const queryClient = useQueryClient();

  const [name, setName] = React.useState("");
  const [selectedFiles, setSelectedFiles] = React.useState<FileRecord[]>([]);
  const [explicitDeviceIds, setExplicitDeviceIds] = React.useState<string[]>(
    [],
  );
  const [customFieldValues, setCustomFieldValues] = React.useState<
    Record<string, unknown>
  >({});
  const [submitError, setSubmitError] = React.useState<React.ReactNode>(null);
  const [outcome, setOutcome] = React.useState<SessionCompositionOutcome>();
  const [creationResult, setCreationResult] =
    React.useState<SessionCreationResult>();
  const [isRecovering, setIsRecovering] = React.useState(false);
  const operation = React.useRef<CreationOperation>();
  const resultRef = React.useRef<SessionCreationResult>();
  const busy = React.useRef(false);
  const mounted = React.useRef(true);
  // The seed a page visit prefills from, and the attempt context built on it:
  // a creation belongs to one seed and one visit, so either changing abandons
  // it rather than letting its result land on a different draft.
  const prefillInputsKey = JSON.stringify([
    orgId,
    datasetId,
    fileIds,
    directoryPatterns,
  ]);
  const contextKey = JSON.stringify([pageKey, prefillInputsKey]);
  const latestContext = React.useRef(contextKey);
  latestContext.current = contextKey;

  // Editing the draft, leaving the page, or navigating to a different seed
  // abandons an attempt still in flight: no further batches are sent, and the
  // session it created is deleted rather than left holding only some of the
  // files.
  const stop = () => {
    if (operation.current) {
      operation.current.stopped = true;
    }
  };

  React.useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (operation.current) {
        operation.current.stopped = true;
      }
    };
  }, []);

  React.useEffect(() => {
    if (operation.current && operation.current.contextKey !== contextKey) {
      operation.current.stopped = true;
    }
  }, [contextKey]);

  // Resolves explicitly-selected files by id through the per-file query
  // cache; concurrent fetches for the same file are deduplicated.
  const { fetchFileRecords } = useFileRecords(fileIds ?? []);

  // Prefill the file selection. When the caller pre-selected files/directories
  // (file-browser bulk selection), prefill exactly those: explicit files are
  // fetched by id, and selected directories are expanded server-side.
  // Otherwise, when a dataset is in scope, prefill all of its files. The
  // top-level no-dataset entry prefills nothing — the user picks files via the
  // FilePicker.
  const prefillQuery = useQuery({
    queryKey: [
      "createSessionPrefillFiles",
      orgId,
      datasetId,
      fileIds,
      directoryPatterns,
    ],
    queryFn: async ({ signal }) => {
      const hasSelection =
        (fileIds?.length ?? 0) > 0 || (directoryPatterns?.length ?? 0) > 0;

      if (!hasSelection) {
        // Whole-dataset entry point: prefill every file in the dataset.
        // `enabled` guarantees datasetId is defined when there is no
        // selection, but guard rather than assert.
        return datasetId === undefined
          ? []
          : datasetService.listAllFiles(datasetId, { abortSignal: signal });
      }

      const [fromFiles, fromDirectories] = await Promise.all([
        fileIds && fileIds.length > 0 ? fetchFileRecords() : [],
        // Directory patterns are dataset-relative; without a dataset in
        // scope they are ignored.
        directoryPatterns &&
        directoryPatterns.length > 0 &&
        datasetId !== undefined
          ? datasetService.listAllFiles(datasetId, {
              includePatterns: directoryPatterns,
              abortSignal: signal,
            })
          : [],
      ]);

      // A file can be both explicitly selected and a member of a selected
      // directory.
      return dedupeByFileId([...fromFiles, ...fromDirectories]);
    },
    enabled: datasetId !== undefined || (fileIds?.length ?? 0) > 0,
  });

  // Latch the prefill per input set: a later refetch of the same inputs
  // (e.g. a stale-then-fresh cache serve) doesn't clobber files the user has
  // already added or removed, while changed inputs (a same-route
  // renavigation carrying a different seed) re-apply the new prefill.
  // Mid-prefill edits can't be clobbered either: FilesSection disables its
  // edit affordances while the prefill is in flight.
  const prefilledForInputsRef = React.useRef<string | null>(null);
  React.useEffect(
    function prefillSelectionOncePerInputs() {
      if (
        prefilledForInputsRef.current === prefillInputsKey ||
        prefillQuery.data === undefined
      ) {
        return;
      }
      setSelectedFiles(prefillQuery.data);
      prefilledForInputsRef.current = prefillInputsKey;
    },
    [prefillInputsKey, prefillQuery.data],
  );

  const {
    deviceIds: inferredDeviceIds,
    isLoading: isInferringDevices,
    error: inferDevicesError,
  } = useInferDevices(
    selectedFiles.map((file) => file.file_id),
    orgId,
  );

  const createMutation = useCreateSessionFromFiles();

  const mergeFiles = (incoming: FileRecord[]) => {
    stop();
    setSelectedFiles((prev) => dedupeByFileId([...prev, ...incoming]));
  };

  const removeFiles = (fileIdsToRemove: string[]) => {
    stop();
    const toRemove = new Set(fileIdsToRemove);
    setSelectedFiles((prev) =>
      prev.filter((file) => !toRemove.has(file.file_id)),
    );
  };

  const addDevice = (deviceId: string) => {
    stop();
    setExplicitDeviceIds((prev) =>
      prev.includes(deviceId) ? prev : [...prev, deviceId],
    );
  };

  const removeDevice = (deviceId: string) => {
    stop();
    setExplicitDeviceIds((prev) => prev.filter((id) => id !== deviceId));
  };

  const submit = async () => {
    if (
      busy.current ||
      operation.current ||
      prefillQuery.isLoading ||
      prefillQuery.error !== null
    ) {
      return;
    }
    setSubmitError(null);
    const request: CreateSessionRequest = {};
    const trimmedName = name.trim();
    if (trimmedName) {
      request.name = trimmedName;
    }
    if (explicitDeviceIds.length > 0) {
      request.device_ids = [...explicitDeviceIds];
    }
    if (Object.keys(customFieldValues).length > 0) {
      request.custom_fields = structuredClone(customFieldValues);
    }

    let prepared: SessionCompositionOutcome;
    try {
      prepared = prepareSessionComposition(
        { operationId: crypto.randomUUID(), orgId },
        selectedFiles.map((file) => ({ file_id: file.file_id })),
      );
    } catch (error) {
      setSubmitError(
        userFacingErrorMessage(error, "Failed to prepare the session."),
      );
      return;
    }
    const captured: CreationOperation = { contextKey, stopped: false };
    operation.current = captured;
    busy.current = true;
    setOutcome(prepared);
    try {
      const result = await createMutation.mutateAsync({
        request,
        outcome: prepared,
        options: {
          stopRequested: () =>
            captured.stopped ||
            !mounted.current ||
            latestContext.current !== captured.contextKey,
          onProgress: (progress) => {
            if (mounted.current && operation.current === captured) {
              setOutcome(progress);
            }
          },
        },
      });
      resultRef.current = result;
      if (mounted.current && operation.current === captured) {
        setOutcome(result.outcome);
        setCreationResult(result);
        if (
          result.kind === "completed" &&
          !captured.stopped &&
          latestContext.current === captured.contextKey
        ) {
          goto.session({ sessionId: result.sessionId });
        }
      }
    } catch (error) {
      // A stopped attempt rejects only when the stop came before the create request went out.
      // Nothing was created then, and the page's creation status already says so, so there is no
      // error to show.
      if (mounted.current && !captured.stopped) {
        setSubmitError(
          userFacingErrorMessage(error, "Failed to create session."),
        );
      }
    } finally {
      busy.current = false;
    }
  };

  const refreshCaches = async (sessionId?: string) => {
    const refreshes = [
      queryClient.invalidateQueries({ queryKey: sessionQueryKeys.lists() }),
    ];
    if (sessionId) {
      refreshes.push(
        queryClient.invalidateQueries({
          queryKey: sessionQueryKeys.detail(sessionId),
        }),
      );
    }
    await Promise.allSettled(refreshes);
  };

  const refreshSessions = async () => {
    if (busy.current) {
      return;
    }
    busy.current = true;
    setIsRecovering(true);
    try {
      await refreshCaches(resultRef.current?.outcome.sessionId);
    } finally {
      busy.current = false;
      if (mounted.current) {
        setIsRecovering(false);
      }
    }
  };

  const checkCreation = async () => {
    const previous = resultRef.current;
    const captured = operation.current;
    if (
      busy.current ||
      !captured ||
      previous?.kind !== "cleanupUnknown" ||
      latestContext.current !== captured.contextKey
    ) {
      return;
    }
    busy.current = true;
    setIsRecovering(true);
    try {
      const checked = await checkSessionCreation(sessionService, previous);
      if (operation.current === captured) {
        resultRef.current = checked;
        if (mounted.current && latestContext.current === captured.contextKey) {
          setCreationResult(checked);
        }
      }
    } finally {
      await refreshCaches(previous.sessionId);
      busy.current = false;
      if (mounted.current) {
        setIsRecovering(false);
      }
    }
  };

  const prepareNewCreation = () => {
    if (busy.current) {
      return;
    }
    operation.current = undefined;
    resultRef.current = undefined;
    setOutcome(undefined);
    setCreationResult(undefined);
    setSubmitError(null);
  };

  return {
    name,
    setName: (value) => {
      stop();
      setName(value);
    },
    selectedFiles,
    isPrefilling: prefillQuery.isLoading,
    prefillError: prefillQuery.error,
    mergeFiles,
    removeFiles,
    inferredDeviceIds,
    isInferringDevices,
    inferDevicesError,
    explicitDeviceIds,
    addDevice,
    removeDevice,
    customFieldValues,
    setCustomFieldValues: (values) => {
      stop();
      setCustomFieldValues(structuredClone(values));
    },
    submitError,
    isSubmitting: createMutation.isPending || (busy.current && !isRecovering),
    submit,
    outcome,
    creationResult,
    isRecovering,
    canSubmit:
      !operation.current &&
      !busy.current &&
      !prefillQuery.isLoading &&
      prefillQuery.error === null,
    contextMatches:
      !operation.current || operation.current.contextKey === contextKey,
    stop,
    checkCreation,
    refreshSessions,
    prepareNewCreation,
  };
}

/** Deduplicates by `file_id`; on a collision the later entry wins. */
function dedupeByFileId(files: FileRecord[]): FileRecord[] {
  const byId = new Map(files.map((file) => [file.file_id, file]));
  return Array.from(byId.values());
}
